/**
 * LocationSearchModal — Full-screen modal for searching pickup location.
 * Uses Mapbox geocoding via geocodingService.forward().
 * Rendered through `AppModal`, which carries the GestureHandlerRootView every
 * modal needs on Android. The RN Pressable/ScrollView below are a deliberate
 * choice for the results list, not a workaround for the missing root.
 */
import { useState, useCallback, useRef, useEffect } from 'react'
import { View, Text, ScrollView as RNScrollView, TextInput as RNTextInput } from 'react-native'
import { AppModal } from '@/components/ui/AppModal'
import { GlassSurface } from '@/components/ui/GlassSurface'
import { LiquidCanvas } from '@/components/ui/LiquidBackdrop'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import { X, MapPin } from '@/components/ui/icons'
import { useSemanticColors } from '../../lib/theme/tokens'
import { Skeleton } from '../ui/Skeleton'
import { COLORS, FONTS, SPACING, RADIUS } from '../../lib/brand'
import { useTheme } from '../../lib/hooks/use-theme'
import { useExploreStore } from '../../lib/stores/explore.store'
import { geocodingService } from '../../lib/services/geocoding.service'
import { TrackedNativePressable as RNPressable } from '../ui/TrackedNativePressable'

interface LocationSearchModalProps {
  visible: boolean
  onClose: () => void
}

interface GeocodingResult {
  lat: number
  lng: number
  address: string
  city: string | null
  country: string | null
}

export function LocationSearchModal({ visible, onClose }: LocationSearchModalProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()

  const [query, setQuery] = useState('')
  const [results, setResults] = useState<GeocodingResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [hasError, setHasError] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inputRef = useRef<RNTextInput>(null)

  // Reset state when modal opens
  useEffect(() => {
    if (!visible) return

    setQuery('')
    setResults([])
    setIsLoading(false)
    setHasSearched(false)
    setHasError(false)
    // Auto-focus after modal animation
    const focusTimer = setTimeout(() => {
      inputRef.current?.focus()
    }, 300)
    return () => clearTimeout(focusTimer)
  }, [visible])

  /**
   * A geocoding call that never reached Mapbox is not the same thing as an
   * address that does not exist. Both used to render "No se encontraron
   * resultados", telling the user their street is not on the map when the
   * request simply failed — and leaving nothing to tap.
   */
  const runSearch = useCallback(async (text: string) => {
    setIsLoading(true)
    try {
      const data = await geocodingService.forward(text)
      setResults(data)
      setHasError(false)
    } catch {
      setResults([])
      setHasError(true)
    } finally {
      setHasSearched(true)
      setIsLoading(false)
    }
  }, [])

  const handleQueryChange = useCallback(
    (text: string) => {
      setQuery(text)

      // Clear previous timer
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }

      if (!text.trim()) {
        setResults([])
        setIsLoading(false)
        setHasSearched(false)
        setHasError(false)
        return
      }

      setIsLoading(true)

      timerRef.current = setTimeout(() => {
        void runSearch(text)
      }, 250)
    },
    [runSearch],
  )

  const handleRetry = useCallback(() => {
    const trimmed = query.trim()
    if (!trimmed) return
    void runSearch(trimmed)
  }, [query, runSearch])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  const handleResultPress = useCallback(
    (result: GeocodingResult) => {
      useExploreStore.getState().setLocation({
        lat: result.lat,
        lng: result.lng,
        name: result.address,
      })
      onClose()
    },
    [onClose],
  )

  return (
    <AppModal
      visible={visible}
      animationType="slide"
      transparent={false}
      onRequestClose={onClose}
      testID="explore.location-search-modal"
    >
      <LiquidCanvas
        isolate
        style={{
          flex: 1,
          paddingTop: insets.top,
        }}
      >
        {/* Header */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            paddingHorizontal: SPACING.lg,
            paddingVertical: SPACING.md,
            borderBottomWidth: 1,
            borderBottomColor: colors.border,
            gap: SPACING.md,
          }}
        >
          <View style={{ flex: 1 }}>
            <Text
              style={{
                fontSize: 20,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
              }}
            >
              {t('explore.location_search_title')}
            </Text>
          </View>
          <RNPressable
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel={t('explore.location_close')}
            testID="explore.location-search-close"
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              backgroundColor: colors.surfaceSubtle,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={18} color={colors.textSecondary} strokeWidth={2} />
          </RNPressable>
        </View>

        {/* Search Input */}
        <View
          style={{
            paddingHorizontal: SPACING.lg,
            paddingVertical: SPACING.md,
          }}
        >
          <GlassSurface
            tone="quiet"
            style={{
              height: 48,
              borderRadius: RADIUS.md,
            }}
            contentStyle={{ justifyContent: 'center', paddingHorizontal: SPACING.md }}
          >
            <RNTextInput
              ref={inputRef}
              value={query}
              onChangeText={handleQueryChange}
              placeholder={t('explore.location_search_placeholder')}
              placeholderTextColor={colors.textMuted}
              autoCorrect={false}
              autoCapitalize="none"
              accessibilityLabel={t('explore.location_search_placeholder')}
              testID="explore.location-search-input"
              style={{
                height: 48,
                fontSize: 16,
                fontFamily: FONTS.body,
                color: colors.textPrimary,
              }}
            />
          </GlassSurface>
        </View>

        {/* Results */}
        <RNScrollView
          contentContainerStyle={{
            paddingHorizontal: SPACING.lg,
            paddingBottom: SPACING.lg,
          }}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {isLoading && (
            <View style={{ gap: SPACING.md, paddingTop: SPACING.sm }}>
              {[0, 1, 2].map((i) => (
                <View
                  key={i}
                  style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.md }}
                >
                  <Skeleton width={36} height={36} borderRadius={RADIUS.sm} />
                  <View style={{ flex: 1, gap: SPACING.xs }}>
                    <Skeleton width="70%" height={14} />
                    <Skeleton width="90%" height={12} />
                  </View>
                </View>
              ))}
            </View>
          )}

          {!isLoading && hasSearched && hasError && (
            <View
              style={{
                paddingVertical: SPACING.xl,
                alignItems: 'center',
                gap: SPACING.md,
              }}
            >
              <Text
                style={{
                  fontSize: 14,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                  textAlign: 'center',
                }}
              >
                {t('explore.location_search_error')}
              </Text>
              <RNPressable
                onPress={handleRetry}
                accessibilityRole="button"
                accessibilityLabel={t('common.retry')}
                testID="explore.location-search-retry"
                style={{
                  minHeight: 44,
                  minWidth: 44,
                  paddingHorizontal: SPACING.lg,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: RADIUS.full,
                  borderWidth: 1,
                  borderColor: colors.border,
                  backgroundColor: colors.surface,
                }}
              >
                <Text
                  style={{
                    fontSize: 14,
                    fontFamily: FONTS.body,
                    color: colors.textBrand,
                  }}
                >
                  {t('common.retry')}
                </Text>
              </RNPressable>
            </View>
          )}

          {!isLoading && hasSearched && !hasError && results.length === 0 && (
            <View
              style={{
                paddingVertical: SPACING.xl,
                alignItems: 'center',
              }}
            >
              <Text
                style={{
                  fontSize: 14,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                }}
              >
                {t('explore.location_no_results')}
              </Text>
            </View>
          )}

          {!isLoading &&
            results.map((result, index) => (
              <RNPressable
                key={`${result.lat}-${result.lng}-${index}`}
                onPress={() => handleResultPress(result)}
                accessibilityRole="button"
                accessibilityLabel={result.address}
                testID={`explore.location-result-${index}`}
                style={({ pressed }) => ({ marginBottom: SPACING.xs, opacity: pressed ? 0.76 : 1 })}
              >
                <GlassSurface
                  tone="regular"
                  radius={RADIUS.md}
                  style={{ minHeight: 56 }}
                  contentStyle={{
                    minHeight: 56,
                    paddingHorizontal: SPACING.md,
                    paddingVertical: SPACING.sm,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: SPACING.md,
                  }}
                >
                  <View
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 18,
                      backgroundColor: colors.surfaceSubtle,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <MapPin size={18} color={COLORS.coral.DEFAULT} strokeWidth={2} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text
                      style={{
                        fontSize: 15,
                        fontFamily: FONTS.body,
                        color: colors.textPrimary,
                      }}
                      numberOfLines={1}
                    >
                      {result.address}
                    </Text>
                    {result.city ? (
                      <Text
                        style={{
                          fontSize: 13,
                          fontFamily: FONTS.body,
                          color: colors.textSecondary,
                          marginTop: SPACING.xs,
                        }}
                        numberOfLines={1}
                      >
                        {result.city}
                      </Text>
                    ) : null}
                  </View>
                </GlassSurface>
              </RNPressable>
            ))}
        </RNScrollView>
      </LiquidCanvas>
    </AppModal>
  )
}
